import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import { useTheme } from 'styled-components';

import clsx from 'clsx';

import useSticky from '@ecp/common/src/hooks/useSticky';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { HorizontalDivider } from '@ecp/common/src/layouts/flex/styled';
import Drawer from '@ecp/common/src/components/modal/Drawer';
import Popover from '@ecp/common/src/components/popover/Popover';
import { MAIN_FUNCTION_TYPE_CODE } from '@ecp/common/src/const/display/mainTemplateConst';

import { userInfoState } from '@fo-recoil/common/user/atom';
import { commonMainFunctionListState, employeeBenefitOpenState } from '@fo-recoil/main/atom';
import { menuListState } from '@fo-recoil/menu/atom';
import mainFunctionApi from '@fo-apis/display/mainFunctionApi';
import CategoryBoard from '@fo-pages/home/gnb/CategoryBoard';
import SideFloatingArea from '@fo-pages/home/gnb/sideFloating/SideFloatingArea';
import EmployeeBenefit from '@fo-pages/home/gnb/sideFloating/EmployeeBenefit';
import SkypassGnbHeader from '@ecp/fo/sites/skypass/components/gnb/SkypassGnbHeader';
import SkypassGnbHeaderBottom from '@ecp/fo/sites/skypass/components/gnb/SkypassGnbHeaderBottom';
import { Container, GnbWrapper, GnbHeaderContainer } from '@ecp/fo/sites/skypass/styles/gnb/SkypassGnbStyles';

export default function SkypassGnb() {
  const theme = useTheme();
  const setCommonMainFunctionList = useSetRecoilState(commonMainFunctionListState);
  const [employeeBenefitOpen, setEmployeeBenefitOpen] = useRecoilState(employeeBenefitOpenState);
  const userInfo = useRecoilValue(userInfoState);

  const [open, setOpen] = useState(false);
  const [gnbList, setGnbList] = useState([]);
  const [searchPhraseList, setSearchPhraseList] = useState([]);
  const [searchPraseListIndex, setSearchPraseListIndex] = useState(0);

  const { ref, sticky } = useSticky();
  const { pathname } = useLocation();
  const className = clsx({ sticky });
  const setMenuList = useSetRecoilState(menuListState);

  const fetchGnbList = useCallback(async () => {
    const { result } = await mainFunctionApi.getGnbList();
    const gnbList = result.map(({ title, bullet, fontColorCode, highLight, pcNewTab, pcUrl }) => ({
      title,
      link: pcUrl,
      dot: bullet,
      bold: highLight,
      color: fontColorCode ? `#${fontColorCode}` : theme.color.text.basic,
      target: pcNewTab ? '_blank' : '_self',
    }));
    setGnbList(gnbList);
    setMenuList(gnbList);
  }, [setGnbList, userInfo]);

  const fetchSearchPhraseList = useCallback(async () => {
    const { result } = await mainFunctionApi.getSearchPhraseList();
    const searchPhraseList = result.map(({ text, pcNewTab, pcUrl }) => ({
      text,
      pcUrl,
      target: pcNewTab ? '_blank' : '_self',
    }));
    setSearchPhraseList(searchPhraseList);
  }, []);

  const fetchCommonMainFunctionList = useCallback(async () => {
    const { result } = await mainFunctionApi.getMainFunctionList(MAIN_FUNCTION_TYPE_CODE.COMMON);
    setCommonMainFunctionList(result);
  }, [setCommonMainFunctionList]);

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([fetchGnbList(), fetchCommonMainFunctionList(), fetchSearchPhraseList()]);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [fetchCommonMainFunctionList, fetchGnbList, fetchSearchPhraseList]);

  useEffect(() => {
    setSearchPraseListIndex(searchPraseListIndex >= searchPhraseList.length - 1 ? 0 : searchPraseListIndex + 1);
  }, [pathname]);

  const hideCategory = () => {
    setOpen(false);
  };

  const handleClose = useCallback(() => {
    setEmployeeBenefitOpen(false);
  }, [setEmployeeBenefitOpen]);

  return (
    <Container className={className}>
      <Popover
        closeWhenOutside
        closeWhenReference
        blockAutoFocus
        placement={'bottom-start'}
        open={open}
        value={<CategoryBoard sticky={sticky} gnbList={gnbList} open={open} onClose={hideCategory} />}
        onOpenChange={hideCategory}
      >
        <GnbWrapper className={className}>
          <GnbHeaderContainer className={className}>
            {!sticky && <Spacing top={23} />}
            <SkypassGnbHeader
              ref={ref}
              searchPhraseList={searchPhraseList}
              searchPraseListIndex={searchPraseListIndex}
            />
            <Spacing top={!sticky ? 19 : 14} />
            <HorizontalDivider width="calc(max(100vw, 1630px))" />
            {!sticky && <Spacing top={26} />}
            <SkypassGnbHeaderBottom gnbList={gnbList} sticky={sticky} />
            <Spacing top={46} />
            <SideFloatingArea />
          </GnbHeaderContainer>
        </GnbWrapper>
      </Popover>
      <Drawer
        placement={'right'}
        open={employeeBenefitOpen}
        onClose={handleClose}
        value={<EmployeeBenefit onClose={handleClose} />}
      />
    </Container>
  );
}
